import { Link } from 'react-router-dom';
import { BellRing, CalendarClock } from 'lucide-react';
import { useNotifications } from '@api/hooks/useNotifications';
import { Card, CardContent, CardHeader, CardTitle } from '@components/common/Card';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import type { NotificationRecord } from '@schemas/api';
import { type DocumentSummaryPayload, formatDeadlineBucket } from '@utils/notifications';

const renderCounts = (notification: NotificationRecord) => {
  const payload = (notification.payload ?? {}) as DocumentSummaryPayload;
  const counts = Object.entries(payload.counts ?? {});

  if (counts.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {counts.map(([bucket, count]) => (
        <span
          key={`${notification.id}-${bucket}`}
          className="rounded-full border border-border-default px-2.5 py-1 text-xs font-medium text-content-secondary"
        >
          {bucket.replace(/_/g, ' ')}: {count}
        </span>
      ))}
    </div>
  );
};

const renderItems = (notification: NotificationRecord) => {
  const payload = (notification.payload ?? {}) as DocumentSummaryPayload;
  const items = payload.items ?? [];

  if (items.length === 0) {
    return (
      <p className="text-sm text-content-secondary">
        This summary was delivered, but it did not include any line items.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {items.slice(0, 4).map((item, index) => (
        <div
          key={`${notification.id}-${item.invoice_id ?? item.ap_posting_id ?? index}`}
          className="rounded-lg border border-border-default p-3"
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="font-medium text-content-primary">
                {item.invoice_number || 'Unnumbered invoice'}
                {item.vendor_name ? ` • ${item.vendor_name}` : ''}
              </p>
              <p className="mt-1 text-sm text-content-secondary">
                {formatDeadlineBucket(item.bucket, item.days_until)}
                {item.org_unit_name ? ` • ${item.org_unit_name}` : ''}
              </p>
            </div>
            {typeof item.amount === 'number' ? (
              <p className="text-sm font-semibold text-content-primary">
                ${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
            {item.invoice_status ? <span>Invoice: {item.invoice_status}</span> : null}
            {item.ap_posting_status ? <span>AP posting: {item.ap_posting_status}</span> : null}
            {item.ap_posting_approval_status ? <span>Approval: {item.ap_posting_approval_status}</span> : null}
          </div>
        </div>
      ))}
      {items.length > 4 ? (
        <p className="text-sm text-content-secondary">
          {items.length - 4} more item{items.length - 4 === 1 ? '' : 's'} in the latest summary.
        </p>
      ) : null}
    </div>
  );
};

export const DeadlineSummariesWidget = () => {
  const { data, isLoading } = useNotifications({
    category: 'document_summary',
    status: 'unread',
    limit: 3,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  const notifications = data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary-500" />
            <CardTitle>Deadline Summaries</CardTitle>
          </div>
          <Link
            to="/notifications?category=document_summary"
            className="text-sm font-medium text-primary-500 hover:text-primary-600"
          >
            Open inbox
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border-default p-6 text-sm text-content-secondary">
            No unread document summaries.
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div key={notification.id} className="rounded-xl border border-border-default p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-content-primary">{notification.title}</p>
                    {notification.body ? (
                      <p className="mt-1 text-sm text-content-secondary">{notification.body}</p>
                    ) : null}
                  </div>
                  <BellRing className="h-4 w-4 text-content-muted" />
                </div>
                {renderCounts(notification)}
                <div className="mt-4">{renderItems(notification)}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
